import {useState, useRef, useEffect} from 'react'
import type {GenericAsset} from '../../../../../../brand/lib/types/contentful'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import {Image} from '../../../../../../components/Image/Image'

import Styles from './YouTubePlayer.module.css'

type Props = {
  youtubeUrl: string
  heading: string
  location: string
  poster?: GenericAsset
  loading?: 'eager' | 'lazy'
}

const isImageAsset = (asset: GenericAsset): boolean => {
  return !!asset.fields.asset.fields.file.details?.image
}

const extractYouTubeId = (url: string): string | null => {
  try {
    const urlObj = new URL(url, 'https://dummy-base.url')
    const hostname = urlObj.hostname.toLowerCase()

    const allowedHosts = ['youtube.com', 'www.youtube.com', 'youtube-nocookie.com']
    if (!allowedHosts.includes(hostname)) {
      return null
    }

    // Handle youtube.com/watch?v=VIDEO_ID
    const videoId = urlObj.searchParams.get('v')
    if (videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
      return videoId
    }

    return null
  } catch {
    return null
  }
}

export function YouTubePlayer(props: Props) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [showOverlay, setShowOverlay] = useState(true)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const {youtubeUrl, heading, location, poster, loading} = props

  const youtubeId = extractYouTubeId(youtubeUrl)

  // Use privacy-enhanced mode URL
  const youtubeHost = `https://www.youtube-nocookie.com/embed/${youtubeId}`
  const youtubeConfig = '?modestbranding=1&rel=0&controls=1&showinfo=0'
  const youtubeSrc = `${youtubeHost}${youtubeConfig}`

  // Poster image
  const image = poster && isImageAsset(poster) ? poster : undefined

  const handlePlayClick = () => {
    if (buttonRef.current) {
      buttonRef.current.setAttribute('data-animate-out', 'true')
      setIsPlaying(true)
    }

    // Use timeout to allow transition to complete
    timeoutRef.current = setTimeout(() => {
      setShowOverlay(false)
    }, 1000)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return youtubeId ? (
    <div className={Styles.YouTubePlayer}>
      {(!image || isPlaying) && (
        <>
          {/* eslint-disable-next-line @eslint-react/dom/no-missing-iframe-sandbox */}
          <iframe
            className={Styles.Iframe}
            title={heading}
            width="1600"
            height="900"
            src={`${youtubeSrc}${image ? '&autoplay=1' : ''}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </>
      )}

      {image && showOverlay && (
        <button
          ref={buttonRef}
          className={Styles.Overlay}
          onClick={handlePlayClick}
          aria-label={`${t('Play video')} ${heading}`}
          {...getAnalyticsEvent({
            action: 'play_youtube_video',
            tag: 'button',
            context: 'youtube_player',
            location,
          })}
        >
          <Image
            className={Styles.PosterImage}
            src={image?.fields.asset.fields.file.url}
            alt={image?.fields.asset.fields.description || ''}
            loading={loading || 'lazy'}
          />

          <div className={Styles.PlayButton}>
            <svg
              className={Styles.PlayButtonIcon}
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="currentColor"
                d="M14.3422 6.87315C15.2193 7.36162 15.2193 8.63838 14.3422 9.12685L5.88073 13.8391C5.03482 14.3102 4 13.6902 4 12.7123L4 3.28772C4 2.30979 5.03482 1.68977 5.88073 2.16087L14.3422 6.87315Z"
              />
            </svg>
          </div>
        </button>
      )}
    </div>
  ) : null
}
